import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { api, buildUrl } from "@shared/routes";
import { apiRequest } from "@/lib/queryClient";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  Building2,
  Users,
  Home,
  Activity,
  Plus,
  Pencil,
  LogOut,
  Briefcase,
  UserPlus,
  Shield,
  CreditCard,
  Receipt,
  FileText,
  DollarSign,
  Calendar,
  Check,
  X,
  Send,
  ToggleRight,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Percent,
  BarChart3,
  ExternalLink,
  Zap,
  KeyRound,
  LogIn,
  Mail,
  Search,
} from "lucide-react";
import { useLocation } from "wouter";
import { FEATURE_KEYS, FEATURE_LABELS } from "@shared/schema";
import { Switch } from "@/components/ui/switch";

const orgFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().min(1, "Slug is required").regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens"),
  status: z.enum(["active", "trial", "suspended"]),
  businessMode: z.enum(["property_management", "service_business", "hybrid"]).default("property_management"),
});

type OrgFormValues = z.infer<typeof orgFormSchema>;

const userFormSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  fullName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  organizationId: z.number({ required_error: "Organization is required" }),
  role: z.string().default("super_admin"),
  isAdmin: z.boolean().default(true),
});

type UserFormValues = z.infer<typeof userFormSchema>;

const MODULE_KEYS = FEATURE_KEYS;
const MODULE_LABELS = FEATURE_LABELS;

const billingPlanSchema = z.object({
  model: z.enum(["flat", "per_apartment", "per_usage", "custom"]),
  currency: z.string().default("USD"),
  flatAmount: z.string().optional(),
  perApartmentAmount: z.string().optional(),
  perUsageAmount: z.string().optional(),
  customAmount: z.string().optional(),
  billingCycle: z.enum(["monthly", "quarterly", "yearly"]).default("monthly"),
  isActive: z.boolean().default(true),
  notes: z.string().optional(),
  modulesEnabled: z.array(z.string()).default([]),
});

type BillingPlanValues = z.infer<typeof billingPlanSchema>;

const invoiceGenerateSchema = z.object({
  periodStart: z.string().min(1, "Start date is required"),
  periodEnd: z.string().min(1, "End date is required"),
  adjustments: z.string().optional(),
  notes: z.string().optional(),
});

type InvoiceGenerateValues = z.infer<typeof invoiceGenerateSchema>;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Active", variant: "default" },
  trial: { label: "Trial", variant: "secondary" },
  suspended: { label: "Suspended", variant: "destructive" },
};

const billingModelLabels: Record<string, string> = {
  flat: "Flat Fee",
  per_apartment: "Per Apartment",
  per_usage: "Per Usage",
  custom: "Custom",
};

const invoiceStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Draft", variant: "outline" },
  issued: { label: "Issued", variant: "secondary" },
  paid: { label: "Paid", variant: "default" },
  overdue: { label: "Overdue", variant: "destructive" },
  void: { label: "Void", variant: "outline" },
};

function BillingPlanDialog({ org, open, onOpenChange }: { org: any; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: plan, isLoading: planLoading } = useQuery<any>({
    queryKey: ['/api/platform/organizations', org.id, 'billing-plan'],
    queryFn: async () => {
      const res = await fetch(buildUrl(api.platform.billing.getPlan.path, { id: org.id }));
      if (!res.ok) throw new Error("Failed to fetch plan");
      return res.json();
    },
    enabled: open,
  });

  const form = useForm<BillingPlanValues>({
    resolver: zodResolver(billingPlanSchema),
    defaultValues: {
      model: "flat",
      currency: "USD",
      flatAmount: "0.00",
      perApartmentAmount: "0.00",
      perUsageAmount: "0.00",
      customAmount: "0.00",
      billingCycle: "monthly",
      isActive: true,
      notes: "",
      modulesEnabled: ["buildings_units", "reservations", "cleaning_jobs", "submissions_checkout", "content_guides", "finance_core", "reports_basic"],
    },
  });

  const selectedModel = form.watch("model");

  const upsertMutation = useMutation({
    mutationFn: async (data: BillingPlanValues) => {
      const url = buildUrl(api.platform.billing.upsertPlan.path, { id: org.id });
      const res = await apiRequest("PUT", url, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'billing-plan'] });
      toast({ title: "Billing plan saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const prevPlanRef = useRef<any>(null);

  useEffect(() => {
    if (plan && !planLoading && plan.model && plan !== prevPlanRef.current) {
      prevPlanRef.current = plan;
      form.reset({
        model: plan.model,
        currency: plan.currency || "USD",
        flatAmount: plan.flatAmount || "0.00",
        perApartmentAmount: plan.perApartmentAmount || "0.00",
        perUsageAmount: plan.perUsageAmount || "0.00",
        customAmount: plan.customAmount || "0.00",
        billingCycle: plan.billingCycle || "monthly",
        isActive: plan.isActive ?? true,
        notes: plan.notes || "",
        modulesEnabled: plan.modulesEnabled || [],
      });
    }
  }, [plan, planLoading]);

  useEffect(() => {
    if (!open) {
      prevPlanRef.current = null;
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Billing Plan - {org.name}</DialogTitle>
          <DialogDescription>Configure the billing model for this organization.</DialogDescription>
        </DialogHeader>
        {planLoading ? (
          <div className="py-4 text-center text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={form.handleSubmit((v) => upsertMutation.mutate(v))} className="space-y-4">
            <div>
              <Label>Billing Model</Label>
              <Select
                value={selectedModel}
                onValueChange={(v) => form.setValue("model", v as BillingPlanValues["model"])}
              >
                <SelectTrigger data-testid="select-billing-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat Monthly Fee</SelectItem>
                  <SelectItem value="per_apartment">Per Apartment</SelectItem>
                  <SelectItem value="per_usage">Per Usage (checkout)</SelectItem>
                  <SelectItem value="custom">Custom Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedModel === "flat" && (
              <div>
                <Label>Monthly Fee ($)</Label>
                <Input {...form.register("flatAmount")} type="number" step="0.01" min="0" data-testid="input-flat-amount" />
              </div>
            )}

            {selectedModel === "per_apartment" && (
              <div>
                <Label>Per Apartment Fee ($)</Label>
                <Input {...form.register("perApartmentAmount")} type="number" step="0.01" min="0" data-testid="input-per-apartment-amount" />
                <p className="text-xs text-muted-foreground mt-1">Charged per active apartment each billing cycle</p>
              </div>
            )}

            {selectedModel === "per_usage" && (
              <div>
                <Label>Per Checkout Fee ($)</Label>
                <Input {...form.register("perUsageAmount")} type="number" step="0.01" min="0" data-testid="input-per-usage-amount" />
                <p className="text-xs text-muted-foreground mt-1">Charged per guest checkout during the billing period</p>
              </div>
            )}

            {selectedModel === "custom" && (
              <div>
                <Label>Custom Amount ($)</Label>
                <Input {...form.register("customAmount")} type="number" step="0.01" min="0" data-testid="input-custom-amount" />
                <p className="text-xs text-muted-foreground mt-1">Set a custom billing amount</p>
              </div>
            )}

            <div>
              <Label>Billing Cycle</Label>
              <Select
                value={form.watch("billingCycle")}
                onValueChange={(v) => form.setValue("billingCycle", v as BillingPlanValues["billingCycle"])}
              >
                <SelectTrigger data-testid="select-billing-cycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold">Modules & Feature Access</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MODULE_KEYS.map((key) => {
                  const currentModules = form.watch("modulesEnabled") || [];
                  const isChecked = currentModules.includes(key);
                  return (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      data-testid={`label-module-${key}`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          const current = form.getValues("modulesEnabled") || [];
                          if (checked) {
                            form.setValue("modulesEnabled", [...current, key], { shouldDirty: true });
                          } else {
                            form.setValue("modulesEnabled", current.filter((m: string) => m !== key), { shouldDirty: true });
                          }
                        }}
                        data-testid={`checkbox-module-${key}`}
                      />
                      {MODULE_LABELS[key] || key}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea {...form.register("notes")} placeholder="Optional notes about this billing arrangement" data-testid="input-billing-notes" />
            </div>

            <Button type="submit" className="w-full" disabled={upsertMutation.isPending} data-testid="button-save-billing-plan">
              {upsertMutation.isPending ? "Saving..." : "Save Billing Plan"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  buildings_units: "Manage properties and unit listings",
  reservations: "Reservation calendar and booking management",
  integrations_ical: "Sync reservations via iCal from Airbnb, Booking.com, etc.",
  cleaning_jobs: "Schedule and track cleaning jobs",
  submissions_checkout: "Guest checkout submissions and check-in/out flows",
  content_guides: "Property guides and bilingual content sections",
  ai_concierge: "AI-powered guest chatbot for self-service support",
  finance_core: "Financial records, cash tracking, and reconciliation",
  reports_basic: "Operational and revenue reports",
  billing_invoices: "Property manager invoices from reconciliation data",
  notifications_push: "Real-time push notifications and in-app alerts",
  support_tickets: "Integrated support ticket management",
  booking_engine: "Public booking pages with rate plans and availability",
  channel_manager: "Multi-channel listing management",
  guest_payments: "Collect payments from guests",
  upsells_addons: "Upsell catalog and add-on orders in booking engine",
  advanced_reports: "Advanced analytics, forecasting, and investor dashboards",
  smart_locks: "Smart lock device management and access code generation",
  custom_fields: "Organization-specific custom fields for apartments and reservations",
  commissions: "Multi-party commission tracking and payouts",
  affiliates: "Affiliate referral codes and commission allocation",
  marketplace: "Public marketplace listing and featured properties",
  contractor_invoices: "Cleaner and maintenance contractor invoice workflows",
  tax_compliance: "Tax configuration per rate plan and 1099 generation",
  data_retention: "Data retention policy and audit logging",
  maintenance_ops: "Maintenance job scheduling, estimates, and approval workflows",
  lumi_ai: "Lumi AI business intelligence assistant with function calling",
  guest_crm: "Guest CRM with multi-channel messaging (chat, email, SMS, WhatsApp)",
  automation_engine: "Workflow rule builder with triggers, actions, and webhooks",
  inventory: "Apartment inventory tracking with zone and condition management",
  cleaning_inventory: "Cleaning checklist and inventory verification step",
  cash_box: "Cash drawer / caja tracking and reconciliation",
  unit_costs: "Per-unit cost tracking by category with vendor management",
  daily_reports: "Automated end-of-day summary email reports",
  forecast: "Revenue forecasting and advanced analytics dashboard",
  integrations_admin: "Third-party integration management panel",
  qa_vision: "AI-powered zone scanning — cleaners verify cleaning quality with their phone camera before leaving",
};

const FEATURE_GROUPS: { label: string; keys: string[] }[] = [
  {
    label: "Core Operations",
    keys: ["buildings_units", "reservations", "cleaning_jobs", "maintenance_ops", "support_tickets", "notifications_push"],
  },
  {
    label: "Guest Experience",
    keys: ["submissions_checkout", "content_guides", "ai_concierge", "booking_engine", "guest_payments", "guest_crm"],
  },
  {
    label: "Finance & Reporting",
    keys: ["finance_core", "billing_invoices", "reports_basic", "advanced_reports", "commissions", "cash_box", "unit_costs", "daily_reports", "forecast", "contractor_invoices"],
  },
  {
    label: "Channels & Integrations",
    keys: ["integrations_ical", "integrations_admin", "channel_manager", "upsells_addons"],
  },
  {
    label: "AI & Automation",
    keys: ["lumi_ai", "automation_engine", "qa_vision"],
  },
  {
    label: "Inventory & Access",
    keys: ["inventory", "cleaning_inventory", "smart_locks", "custom_fields"],
  },
  {
    label: "Compliance & Growth",
    keys: ["affiliates", "marketplace", "tax_compliance", "data_retention"],
  },
];

function FeatureFlagsDialog({ org, open, onOpenChange }: { org: any; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data: features = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/platform/organizations', org.id, 'features'],
    queryFn: async () => {
      const res = await fetch(buildUrl(api.platform.features.get.path, { id: org.id }));
      if (!res.ok) throw new Error("Failed to fetch features");
      return res.json();
    },
    enabled: open,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ featureKey, enabled }: { featureKey: string; enabled: boolean }) => {
      const url = buildUrl(api.platform.features.set.path, { id: org.id });
      const res = await apiRequest("PUT", url, { featureKey, enabled });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'features'] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const enableAllMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const url = buildUrl(api.platform.features.bulkSet.path, { id: org.id });
      const res = await apiRequest("PUT", url, {
        features: FEATURE_KEYS.map((key) => ({ featureKey: key, enabled })),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'features'] });
      toast({ title: "Features updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const featureMap = new Map(features.map((f: any) => [f.featureKey, f.enabled]));

  const isFeatureEnabled = (key: string) => {
    if (featureMap.has(key)) return featureMap.get(key);
    return true;
  };

  const enabledCount = FEATURE_KEYS.filter(k => isFeatureEnabled(k)).length;
  const totalCount = FEATURE_KEYS.length;

  const q = search.toLowerCase().trim();
  const filteredGroups = FEATURE_GROUPS.map(group => ({
    ...group,
    keys: group.keys.filter(k =>
      !q ||
      (FEATURE_LABELS[k] || k).toLowerCase().includes(q) ||
      (FEATURE_DESCRIPTIONS[k] || "").toLowerCase().includes(q)
    ),
  })).filter(g => g.keys.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ToggleRight className="w-5 h-5 text-primary" />
            Feature Access — {org.name}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between mt-1">
            <span>Enable or disable modules for this organization.</span>
            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {enabledCount} / {totalCount} enabled
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 border-b shrink-0 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search features..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="input-feature-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => enableAllMutation.mutate(true)} disabled={enableAllMutation.isPending} data-testid="button-enable-all-features" className="shrink-0">
            All On
          </Button>
          <Button variant="outline" size="sm" onClick={() => enableAllMutation.mutate(false)} disabled={enableAllMutation.isPending} data-testid="button-disable-all-features" className="shrink-0">
            All Off
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading features...</div>
          ) : filteredGroups.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">No features match your search.</div>
          ) : (
            <div className="space-y-6">
              {filteredGroups.map((group) => (
                <div key={group.label}>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{group.label}</h4>
                  <div className="space-y-1">
                    {group.keys.map((key) => {
                      const enabled = isFeatureEnabled(key);
                      return (
                        <div
                          key={key}
                          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${enabled ? "bg-green-50 dark:bg-green-950/20" : "bg-muted/40"}`}
                          data-testid={`feature-row-${key}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium leading-tight">{FEATURE_LABELS[key] || key}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{FEATURE_DESCRIPTIONS[key]}</div>
                          </div>
                          <Switch
                            checked={enabled}
                            onCheckedChange={(checked) => toggleMutation.mutate({ featureKey: key, enabled: checked })}
                            disabled={toggleMutation.isPending}
                            data-testid={`switch-feature-${key}`}
                            className="shrink-0"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceGenerateDialog({ org, open, onOpenChange }: { org: any; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const form = useForm<InvoiceGenerateValues>({
    resolver: zodResolver(invoiceGenerateSchema),
    defaultValues: {
      periodStart: firstOfMonth.toISOString().split('T')[0],
      periodEnd: lastOfMonth.toISOString().split('T')[0],
      adjustments: "0",
      notes: "",
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (data: InvoiceGenerateValues) => {
      const url = buildUrl(api.platform.billing.generateInvoice.path, { id: org.id });
      const res = await apiRequest("POST", url, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'invoices'] });
      queryClient.invalidateQueries({ queryKey: [api.platform.billing.allInvoices.path] });
      onOpenChange(false);
      toast({ title: "Invoice generated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Invoice - {org.name}</DialogTitle>
          <DialogDescription>Create an invoice for the specified billing period.</DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => generateMutation.mutate(v))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Period Start</Label>
              <Input type="date" {...form.register("periodStart")} data-testid="input-period-start" />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" {...form.register("periodEnd")} data-testid="input-period-end" />
            </div>
          </div>
          <div>
            <Label>Adjustments ($)</Label>
            <Input {...form.register("adjustments")} type="number" step="0.01" data-testid="input-adjustments" />
            <p className="text-xs text-muted-foreground mt-1">Positive for surcharges, negative for discounts</p>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea {...form.register("notes")} placeholder="Optional invoice notes" data-testid="input-invoice-notes" />
          </div>
          <Button type="submit" className="w-full" disabled={generateMutation.isPending} data-testid="button-generate-invoice">
            {generateMutation.isPending ? "Generating..." : "Generate Invoice"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrgInvoicesDialog({ org, open, onOpenChange }: { org: any; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [syncStripeEmail, setSyncStripeEmail] = useState(org.billingEmail || "");

  const { data: invoices = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/platform/organizations', org.id, 'invoices'],
    queryFn: async () => {
      const res = await fetch(buildUrl(api.platform.billing.invoices.path, { id: org.id }));
      if (!res.ok) throw new Error("Failed to fetch invoices");
      return res.json();
    },
    enabled: open,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ invoiceId, status }: { invoiceId: number; status: string }) => {
      const url = buildUrl(api.platform.billing.updateInvoice.path, { id: invoiceId });
      const res = await apiRequest("PATCH", url, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'invoices'] });
      queryClient.invalidateQueries({ queryKey: [api.platform.billing.allInvoices.path] });
      toast({ title: "Invoice updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const sendToStripeMutation = useMutation({
    mutationFn: async ({ invoiceId, resend }: { invoiceId: number; resend?: boolean }) => {
      const res = await apiRequest("POST", `/api/platform/billing-invoices/${invoiceId}/send-to-stripe`, { resend: resend ?? false });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to send to Stripe");
      }
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/organizations', org.id, 'invoices'] });
      queryClient.invalidateQueries({ queryKey: [api.platform.billing.allInvoices.path] });
      toast({ title: vars.resend ? "Invoice resent via Stripe" : "Invoice sent to Stripe", description: "The invoice has been finalized and sent to the customer." });
    },
    onError: (err: Error) => {
      toast({ title: "Stripe Error", description: err.message, variant: "destructive" });
    },
  });

  const syncCustomerMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await apiRequest("POST", `/api/platform/organizations/${org.id}/stripe-customer`, { email });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create Stripe customer");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.platform.organizations.list.path] });
      toast({ title: "Stripe Customer Created", description: `Customer ID: ${data.stripeCustomerId}` });
    },
    onError: (err: Error) => {
      toast({ title: "Stripe Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invoices - {org.name}</DialogTitle>
          <DialogDescription>View and manage billing invoices for this organization.</DialogDescription>
        </DialogHeader>

        {!org.stripeCustomerId && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> No Stripe Customer linked
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">Link a Stripe customer to send invoices directly to Stripe.</p>
            <div className="flex gap-2">
              <input
                type="email"
                className="flex-1 text-sm border rounded px-2 py-1 bg-white dark:bg-card"
                placeholder="Billing email"
                value={syncStripeEmail}
                onChange={e => setSyncStripeEmail(e.target.value)}
                data-testid="input-stripe-customer-email"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncCustomerMutation.mutate(syncStripeEmail)}
                disabled={syncCustomerMutation.isPending || !syncStripeEmail}
                data-testid="button-sync-stripe-customer"
              >
                {syncCustomerMutation.isPending ? "Creating..." : "Link Stripe"}
              </Button>
            </div>
          </div>
        )}
        {org.stripeCustomerId && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-2 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-emerald-600" />
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              Stripe Customer: <span className="font-mono font-medium">{org.stripeCustomerId}</span>
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="py-4 text-center text-muted-foreground">Loading...</div>
        ) : invoices.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">No invoices yet. Generate one from the billing setup.</div>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv: any) => {
              const sc = invoiceStatusConfig[inv.status] || invoiceStatusConfig.draft;
              return (
                <Card key={inv.id} data-testid={`card-invoice-${inv.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-medium" data-testid={`text-invoice-total-${inv.id}`}>
                          ${parseFloat(inv.total || '0').toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(inv.periodStart).toLocaleDateString("en-US", { timeZone: "UTC" })} - {new Date(inv.periodEnd).toLocaleDateString("en-US", { timeZone: "UTC" })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {inv.stripeInvoiceId && (
                          <Badge variant="outline" className="no-default-active-elevate text-violet-700 border-violet-300 bg-violet-50 dark:bg-violet-950/20 gap-1" data-testid={`badge-stripe-${inv.id}`}>
                            <Zap className="w-2.5 h-2.5" /> Stripe
                          </Badge>
                        )}
                        <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-invoice-status-${inv.id}`}>
                          {sc.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <p>Model: {billingModelLabels[inv.model] || inv.model}</p>
                      {inv.apartmentCount > 0 && <p>Apartments: {inv.apartmentCount}</p>}
                      {inv.usageCount > 0 && <p>Checkouts: {inv.usageCount}</p>}
                      <p>Subtotal: ${parseFloat(inv.subtotal || '0').toFixed(2)}</p>
                      {parseFloat(inv.adjustments || '0') !== 0 && <p>Adjustments: ${parseFloat(inv.adjustments || '0').toFixed(2)}</p>}
                      {inv.dueDate && <p>Due: {new Date(inv.dueDate).toLocaleDateString("en-US", { timeZone: "UTC" })}</p>}
                      {inv.notes && <p>Notes: {inv.notes}</p>}
                      {inv.stripeInvoiceId && <p className="font-mono">Stripe ID: {inv.stripeInvoiceId}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {inv.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateStatusMutation.mutate({ invoiceId: inv.id, status: 'issued' })}
                          disabled={updateStatusMutation.isPending}
                          data-testid={`button-issue-invoice-${inv.id}`}
                        >
                          <Send className="w-3 h-3 mr-1" /> Issue
                        </Button>
                      )}
                      {org.stripeCustomerId && inv.status !== 'void' && inv.status !== 'paid' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-violet-300 text-violet-700 hover:bg-violet-50"
                          onClick={() => sendToStripeMutation.mutate({ invoiceId: inv.id, resend: !!inv.stripeInvoiceId })}
                          disabled={sendToStripeMutation.isPending}
                          data-testid={`button-send-stripe-${inv.id}`}
                        >
                          <Zap className="w-3 h-3 mr-1" /> {sendToStripeMutation.isPending ? "Sending..." : inv.stripeInvoiceId ? "Resend via Stripe" : "Send via Stripe"}
                        </Button>
                      )}
                      {inv.stripeInvoiceUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          data-testid={`button-stripe-link-${inv.id}`}
                        >
                          <a href={inv.stripeInvoiceUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3 h-3 mr-1" /> View in Stripe
                          </a>
                        </Button>
                      )}
                      {(inv.status === 'issued' || inv.status === 'overdue') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateStatusMutation.mutate({ invoiceId: inv.id, status: 'paid' })}
                          disabled={updateStatusMutation.isPending}
                          data-testid={`button-pay-invoice-${inv.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" /> Mark Paid
                        </Button>
                      )}
                      {inv.status !== 'void' && inv.status !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateStatusMutation.mutate({ invoiceId: inv.id, status: 'void' })}
                          disabled={updateStatusMutation.isPending}
                          data-testid={`button-void-invoice-${inv.id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Void
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const AMENITY_OPTIONS = ["wifi", "pool", "gym", "kitchen", "parking", "ac", "washer", "ocean-view", "city-view", "bay-view", "elevator", "concierge"];
const PROPERTY_TYPES = ["apartment", "house", "villa", "studio", "penthouse", "loft", "cabin", "condo"];

function MarketplaceEditDialog({ apartment, open, onOpenChange, onSave, isPending }: {
  apartment: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any) => void;
  isPending: boolean;
}) {
  const [formData, setFormData] = useState({
    marketplaceDescription: apartment?.marketplaceDescription || "",
    marketplacePrice: apartment?.marketplacePrice || "",
    propertyType: apartment?.propertyType || "",
    amenities: apartment?.amenities || "",
    location: apartment?.location || "",
    marketplaceFeaturedUntil: apartment?.marketplaceFeaturedUntil ? new Date(apartment.marketplaceFeaturedUntil).toISOString().split('T')[0] : "",
  });

  useEffect(() => {
    if (apartment && open) {
      setFormData({
        marketplaceDescription: apartment.marketplaceDescription || "",
        marketplacePrice: apartment.marketplacePrice || "",
        propertyType: apartment.propertyType || "",
        amenities: apartment.amenities || "",
        location: apartment.location || "",
        marketplaceFeaturedUntil: apartment.marketplaceFeaturedUntil ? new Date(apartment.marketplaceFeaturedUntil).toISOString().split('T')[0] : "",
      });
    }
  }, [apartment, open]);

  const currentAmenities = formData.amenities ? formData.amenities.split(",").filter(Boolean) : [];

  const toggleAmenity = (amenity: string) => {
    const updated = currentAmenities.includes(amenity)
      ? currentAmenities.filter((a: string) => a !== amenity)
      : [...currentAmenities, amenity];
    setFormData(prev => ({ ...prev, amenities: updated.join(",") }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="text-mp-edit-title">Edit Marketplace Listing</DialogTitle>
          <DialogDescription>
            {apartment?.name} - {apartment?.organizationName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Location</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="City, Country"
              data-testid="input-mp-location"
            />
          </div>
          <div>
            <Label>Marketplace Price (per night)</Label>
            <Input
              type="number"
              value={formData.marketplacePrice}
              onChange={(e) => setFormData(prev => ({ ...prev, marketplacePrice: e.target.value }))}
              placeholder="0.00"
              data-testid="input-mp-price"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.marketplaceDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, marketplaceDescription: e.target.value }))}
              placeholder="Describe this property for marketplace visitors..."
              rows={3}
              data-testid="input-mp-description"
            />
          </div>
          <div>
            <Label>Property Type</Label>
            <Select value={formData.propertyType || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, propertyType: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="select-mp-property-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {PROPERTY_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Featured Until (optional)</Label>
            <Input
              type="date"
              value={formData.marketplaceFeaturedUntil}
              onChange={(e) => setFormData(prev => ({ ...prev, marketplaceFeaturedUntil: e.target.value }))}
              data-testid="input-mp-featured-until"
            />
          </div>
          <div>
            <Label className="mb-2 block">Amenities</Label>
            <div className="flex flex-wrap gap-2">
              {AMENITY_OPTIONS.map(amenity => (
                <Badge
                  key={amenity}
                  variant={currentAmenities.includes(amenity) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleAmenity(amenity)}
                  data-testid={`badge-mp-amenity-${amenity}`}
                >
                  {amenity}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-mp-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => onSave({
                ...formData,
                marketplaceFeaturedUntil: formData.marketplaceFeaturedUntil || null,
                marketplacePrice: formData.marketplacePrice || null,
                propertyType: formData.propertyType || null,
                amenities: formData.amenities || null,
                location: formData.location || null,
                marketplaceDescription: formData.marketplaceDescription || null,
              })}
              disabled={isPending}
              data-testid="button-mp-save"
            >
              {isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformDashboard() {
  const { logout } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [billingOrgOpen, setBillingOrgOpen] = useState(false);
  const [billingOrg, setBillingOrg] = useState<any>(null);
  const [invoiceGenOrgOpen, setInvoiceGenOrgOpen] = useState(false);
  const [invoiceGenOrg, setInvoiceGenOrg] = useState<any>(null);
  const [invoicesOrgOpen, setInvoicesOrgOpen] = useState(false);
  const [invoicesOrg, setInvoicesOrg] = useState<any>(null);
  const [featuresOrgOpen, setFeaturesOrgOpen] = useState(false);
  const [featuresOrg, setFeaturesOrg] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'marketplace' | 'users' | 'leads'>('overview');
  const [editMarketplaceApt, setEditMarketplaceApt] = useState<any>(null);
  const [editMarketplaceOpen, setEditMarketplaceOpen] = useState(false);
  const [selectedOrgUsers, setSelectedOrgUsers] = useState<number | null>(null);
  const [viewProfileUser, setViewProfileUser] = useState<any>(null);
  const [impersonatingOrgId, setImpersonatingOrgId] = useState<number | null>(null);
  const [resendOrgOpen, setResendOrgOpen] = useState(false);
  const [resendOrg, setResendOrg] = useState<any>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [changePassForm, setChangePassForm] = useState({ current: "", next: "", confirm: "" });
  const [changePassLoading, setChangePassLoading] = useState(false);

  const { data: stats, isLoading: statsLoading } = useQuery<{
    totalOrganizations: number;
    activeOrganizations: number;
    totalApartments: number;
    totalUsers: number;
  }>({
    queryKey: [api.platform.stats.path],
  });

  const { data: organizations = [], isLoading: orgsLoading } = useQuery<any[]>({
    queryKey: [api.platform.organizations.list.path],
  });

  const { data: platformUsers = [], isLoading: usersLoading } = useQuery<any[]>({
    queryKey: [api.platform.users.list.path],
  });

  const { data: allInvoices = [] } = useQuery<any[]>({
    queryKey: [api.platform.billing.allInvoices.path],
  });

  const { data: orgUsers = [], isLoading: orgUsersLoading } = useQuery<any[]>({
    queryKey: ['/api/platform/organizations', selectedOrgUsers, 'users'],
    queryFn: async () => {
      if (!selectedOrgUsers) return [];
      const res = await fetch(`/api/platform/organizations/${selectedOrgUsers}/users`);
      if (!res.ok) throw new Error("Failed to fetch org users");
      return res.json();
    },
    enabled: activeTab === 'users' && selectedOrgUsers !== null,
  });

  const { data: viewProfileData } = useQuery<any>({
    queryKey: ['/api/platform/users', viewProfileUser?.id, 'profile'],
    queryFn: async () => {
      if (!viewProfileUser?.id) return null;
      const res = await fetch(`/api/platform/users/${viewProfileUser.id}/profile`);
      if (!res.ok) throw new Error("Failed to fetch profile");
      return res.json();
    },
    enabled: viewProfileUser !== null,
  });

  const { data: econStats } = useQuery<any>({
    queryKey: ['/api/platform/economic-stats'],
    enabled: activeTab === 'overview',
  });

  const { data: platformSettingsData } = useQuery<Record<string, string>>({
    queryKey: ['/api/platform/settings'],
  });

  const toggleRegistrationMutation = useMutation({
    mutationFn: async (open: boolean) => {
      const res = await apiRequest("PATCH", "/api/platform/settings", { registration_open: open ? "true" : "false" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/platform/settings'] });
      toast({ title: "Registration " + (platformSettingsData?.registration_open === "true" ? "closed" : "opened") });
    },
  });

  const { data: hostLeads = [] } = useQuery<any[]>({
    queryKey: ['/api/platform/host-leads'],
    enabled: activeTab === 'leads',
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await fetch(`/api/platform/host-leads/${id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['/api/platform/host-leads'] }); },
  });

  const { data: marketplaceApts = [], isLoading: marketplaceLoading } = useQuery<any[]>({
    queryKey: [api.platform.marketplace.list.path],
    enabled: activeTab === 'marketplace',
  });

  const { data: marketplaceStats } = useQuery<any>({
    queryKey: [api.platform.marketplace.stats.path],
    enabled: activeTab === 'marketplace',
  });

  const [marketplaceOrgFilter, setMarketplaceOrgFilter] = useState<string>("all");
  const [marketplaceVisFilter, setMarketplaceVisFilter] = useState<string>("all");

  const toggleMarketplaceMutation = useMutation({
    mutationFn: async ({ id, field, value }: { id: number; field: string; value: any }) => {
      const res = await apiRequest("PATCH", `/api/platform/marketplace/apartments/${id}`, { [field]: value });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.platform.marketplace.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.platform.marketplace.stats.path] });
      toast({ title: "Listing updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMarketplaceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/platform/marketplace/apartments/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.platform.marketplace.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.platform.marketplace.stats.path] });
      setEditMarketplaceOpen(false);
      toast({ title: "Listing updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const createOrgMutation = useMutation({
    mutationFn: async (data: OrgFormValues) => {
      const res = await apiRequest("POST", api.platform.organizations.create.path, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.platform.organizations.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.platform.stats.path] });
      setCreateOrgOpen(false);
      toast({ title: "Organization created" });
      createOrgForm.reset({ name: "", slug: "", status: "active", businessMode: "property_management" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<OrgFormValues> }) => {
      const url = buildUrl(api.platform.organizations.update.path, { id });
      const res = await apiRequest("PUT", url, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.platform.organizations.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.platform.stats.path] });
      setEditOrgOpen(false);
      setEditingOrg(null);
      toast({ title: "Organization updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (data: UserFormValues) => {
      const res = await apiRequest("POST", api.platform.users.create.path, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.platform.users.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.platform.stats.path] });
      setCreateUserOpen(false);
      toast({ title: "User created" });
      createUserForm.reset({ username: "", password: "", fullName: "", email: "", phone: "", organizationId: undefined as any, role: "super_admin", isAdmin: true });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const createOrgForm = useForm<OrgFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: { name: "", slug: "", status: "active", businessMode: "property_management" },
  });

  const editOrgForm = useForm<OrgFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: { name: "", slug: "", status: "active", businessMode: "property_management" },
  });

  const createUserForm = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: { username: "", password: "", fullName: "", email: "", phone: "", organizationId: undefined as any, role: "super_admin", isAdmin: true },
  });

  const handleCreateOrg = (values: OrgFormValues) => {
    createOrgMutation.mutate(values);
  };

  const handleEditOrg = (org: any) => {
    setEditingOrg(org);
    editOrgForm.reset({
      name: org.name,
      slug: org.slug,
      status: org.status,
      businessMode: org.businessMode || "property_management",
    });
    setEditOrgOpen(true);
  };

  const handleUpdateOrg = (values: OrgFormValues) => {
    if (!editingOrg) return;
    updateOrgMutation.mutate({ id: editingOrg.id, data: values });
  };

  const handleToggleStatus = (org: any) => {
    const newStatus = org.status === "suspended" ? "active" : "suspended";
    updateOrgMutation.mutate({ id: org.id, data: { status: newStatus } });
  };

  const handleCreateUser = (values: UserFormValues) => {
    createUserMutation.mutate(values);
  };

  const getUserCountForOrg = (orgId: number) => {
    return platformUsers.filter((u: any) => u.organizationId === orgId).length;
  };

  const getOrgName = (orgId: number | null) => {
    if (!orgId) return "---";
    const org = organizations.find((o: any) => o.id === orgId);
    return org ? org.name : "Unknown";
  };

  const openBillingPlan = (org: any) => {
    setBillingOrg(org);
    setBillingOrgOpen(true);
  };

  const openInvoiceGen = (org: any) => {
    setInvoiceGenOrg(org);
    setInvoiceGenOrgOpen(true);
  };

  const openInvoices = (org: any) => {
    setInvoicesOrg(org);
    setInvoicesOrgOpen(true);
  };

  const openFeatures = (org: any) => {
    setFeaturesOrg(org);
    setFeaturesOrgOpen(true);
  };

  const handleEnterAsAdmin = async (org: any) => {
    setImpersonatingOrgId(org.id);
    try {
      const res = await apiRequest("POST", `/api/platform/impersonate/${org.id}`, {});
      if (!res.ok) throw new Error("Failed to impersonate");
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      navigate("/admin");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setImpersonatingOrgId(null);
    }
  };

  const handleResendOnboarding = async () => {
    if (!resendOrg) return;
    try {
      const res = await apiRequest("POST", `/api/platform/organizations/${resendOrg.id}/resend-onboarding`, resendEmail ? { to: resendEmail } : {});
      if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      const d = await res.json();
      toast({ title: `Onboarding email sent to ${d.sentTo}` });
      setResendOrgOpen(false);
      setResendOrg(null);
      setResendEmail("");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleChangePassword = async () => {
    if (changePassForm.next !== changePassForm.confirm) {
      toast({ title: "Passwords don't match", variant: "destructive" }); return;
    }
    if (changePassForm.next.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" }); return;
    }
    setChangePassLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/change-password", { currentPassword: changePassForm.current, newPassword: changePassForm.next });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      toast({ title: "Password changed successfully" });
      setChangePassOpen(false);
      setChangePassForm({ current: "", next: "", confirm: "" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setChangePassLoading(false);
    }
  };

  const totalBilled = allInvoices.reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);
  const totalPaid = allInvoices.filter((i: any) => i.status === 'paid').reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);
  const totalOutstanding = allInvoices.filter((i: any) => ['issued', 'overdue'].includes(i.status)).reduce((sum: number, inv: any) => sum + parseFloat(inv.total || '0'), 0);

  const statCards = [
    { title: "Total Organizations", value: stats?.totalOrganizations ?? 0, icon: Building2, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-950" },
    { title: "Active Organizations", value: stats?.activeOrganizations ?? 0, icon: Activity, color: "text-green-500", bg: "bg-green-50 dark:bg-green-950" },
    { title: "Total Apartments", value: stats?.totalApartments ?? 0, icon: Home, color: "text-purple-500", bg: "bg-purple-50 dark:bg-purple-950" },
    { title: "Total Users", value: stats?.totalUsers ?? 0, icon: Users, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-950" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(30 20% 96%) 100%)" }}>
      <header className="sticky top-0 z-50 h-16 border-b bg-white/80 dark:bg-card/80 backdrop-blur-md flex items-center px-6 justify-between gap-4">
        <div className="flex items-center gap-2">
          <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
          <span className="font-display font-bold text-xl" data-testid="text-platform-title">Staio Platform</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setChangePassOpen(true)} data-testid="button-change-password">
            <KeyRound className="h-4 w-4 mr-2" />
            Security
          </Button>
          <Button variant="ghost" className="text-destructive" onClick={() => logout()} data-testid="button-logout">
            <LogOut className="h-5 w-5 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-dashboard-heading">Platform Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage all organizations and users across the platform.</p>
        </div>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-none shadow-sm" data-testid={`card-stat-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-6 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <h3 className="text-3xl font-bold mt-2" data-testid={`text-stat-value-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    {statsLoading ? "..." : stat.value}
                  </h3>
                </div>
                <div className={`p-3 rounded-full ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs for Organizations/Invoices */}
        <div className="flex items-center gap-2 border-b">
          <Button
            variant="ghost"
            size="sm"
            className={`rounded-none border-b-2 ${activeTab === 'overview' ? 'border-primary' : 'border-transparent'}`}
            onClick={() => setActiveTab('overview')}
            data-testid="tab-overview"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Organizations
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`rounded-none border-b-2 ${activeTab === 'invoices' ? 'border-primary' : 'border-transparent'}`}
            onClick={() => setActiveTab('invoices')}
            data-testid="tab-invoices"
          >
            <Receipt className="w-4 h-4 mr-2" />
            All Invoices
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`rounded-none border-b-2 ${activeTab === 'users' ? 'border-primary' : 'border-transparent'}`}
            onClick={() => setActiveTab('users')}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 mr-2" />
            Accounts
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`rounded-none border-b-2 ${activeTab === 'leads' ? 'border-primary' : 'border-transparent'}`}
            onClick={() => setActiveTab('leads')}
            data-testid="tab-leads"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Leads
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`rounded-none border-b-2 ${activeTab === 'marketplace' ? 'border-primary' : 'border-transparent'}`}
            onClick={() => setActiveTab('marketplace')}
            data-testid="tab-marketplace"
          >
            <Home className="w-4 h-4 mr-2" />
            Marketplace
          </Button>
        </div>

        {activeTab === 'overview' && (
          <>
            <Card className="border-none shadow-sm" data-testid="card-platform-settings">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Public Registration</p>
                    <p className="text-xs text-muted-foreground">Allow new property managers to sign up on their own</p>
                  </div>
                </div>
                <Switch
                  data-testid="switch-registration-open"
                  checked={platformSettingsData?.registration_open === "true"}
                  disabled={toggleRegistrationMutation.isPending}
                  onCheckedChange={(checked) => toggleRegistrationMutation.mutate(checked)}
                />
              </CardContent>
            </Card>

            {econStats && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-display font-bold" data-testid="text-economic-impact-heading">Economic Impact</h2>
                  <p className="text-sm text-muted-foreground">Investor-grade financial and operations overview</p>
                </div>

                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  <Card className="border-none shadow-sm" data-testid="card-econ-total-revenue">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-total-revenue">
                          ${(econStats.totalRevenue ?? 0).toLocaleString()}
                        </h3>
                        {econStats.revenueGrowth != null && (
                          <Badge
                            variant={econStats.revenueGrowth >= 0 ? "default" : "destructive"}
                            className="no-default-active-elevate mt-1"
                            data-testid="badge-revenue-growth"
                          >
                            {econStats.revenueGrowth >= 0 ? (
                              <TrendingUp className="w-3 h-3 mr-1" />
                            ) : (
                              <TrendingDown className="w-3 h-3 mr-1" />
                            )}
                            {econStats.revenueGrowth >= 0 ? '+' : ''}{econStats.revenueGrowth}%
                          </Badge>
                        )}
                      </div>
                      <div className="p-3 rounded-full bg-green-50 dark:bg-green-950">
                        <DollarSign className="w-6 h-6 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-this-month-revenue">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">This Month Revenue</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-this-month-revenue">
                          ${(econStats.thisMonthRevenue ?? 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-emerald-50 dark:bg-emerald-950">
                        <TrendingUp className="w-6 h-6 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-avg-revenue-booking">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Avg Revenue per Booking</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-avg-revenue-booking">
                          ${(econStats.avgRevenuePerBooking ?? 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-blue-50 dark:bg-blue-950">
                        <BarChart3 className="w-6 h-6 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  <Card className="border-none shadow-sm" data-testid="card-econ-total-reservations">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Reservations</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-total-reservations">
                          {econStats.totalReservations ?? 0}
                        </h3>
                        {econStats.thisMonthReservations != null && (
                          <p className="text-xs text-muted-foreground mt-1">{econStats.thisMonthReservations} this month</p>
                        )}
                      </div>
                      <div className="p-3 rounded-full bg-orange-50 dark:bg-orange-950">
                        <Calendar className="w-6 h-6 text-orange-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-occupancy-rate">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Occupancy Rate</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-occupancy-rate">
                          {econStats.occupancyRate ?? 0}%
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-amber-50 dark:bg-amber-950">
                        <Percent className="w-6 h-6 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-active-reservations">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Active Reservations</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-active-reservations">
                          {econStats.activeReservations ?? 0}
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-teal-50 dark:bg-teal-950">
                        <Activity className="w-6 h-6 text-teal-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  <Card className="border-none shadow-sm" data-testid="card-econ-cleaning-jobs">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Cleaning Jobs</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-cleaning-jobs">
                          {econStats.totalCleaningJobs ?? 0}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">{econStats.completedCleaningJobs ?? 0} completed</p>
                      </div>
                      <div className="p-3 rounded-full bg-cyan-50 dark:bg-cyan-950">
                        <Check className="w-6 h-6 text-cyan-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-marketplace-listings">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Marketplace Listings</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-marketplace-listings">
                          {econStats.marketplaceListings ?? 0}
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-orange-50 dark:bg-orange-950">
                        <Home className="w-6 h-6 text-orange-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm" data-testid="card-econ-avg-revenue-unit">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Avg Revenue per Unit</p>
                        <h3 className="text-2xl font-bold mt-1" data-testid="text-econ-avg-revenue-unit">
                          ${(econStats.avgRevenuePerApt ?? 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className="p-3 rounded-full bg-violet-50 dark:bg-violet-950">
                        <Building2 className="w-6 h-6 text-violet-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {econStats.orgBreakdown && econStats.orgBreakdown.length > 0 && (
                  <Card className="border-none shadow-sm" data-testid="card-org-revenue-breakdown">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-display font-bold mb-4" data-testid="text-org-breakdown-heading">Organization Revenue Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="pb-2 pr-4 font-medium">Organization</th>
                              <th className="pb-2 pr-4 font-medium">Status</th>
                              <th className="pb-2 pr-4 font-medium text-right">Apartments</th>
                              <th className="pb-2 pr-4 font-medium text-right">Reservations</th>
                              <th className="pb-2 pr-4 font-medium text-right">Total Revenue</th>
                              <th className="pb-2 font-medium text-right">This Month</th>
                            </tr>
                          </thead>
                          <tbody>
                            {econStats.orgBreakdown.map((org: any, idx: number) => {
                              const sc = statusConfig[org.status] || statusConfig.active;
                              return (
                                <tr key={idx} className="border-b last:border-0" data-testid={`row-org-breakdown-${idx}`}>
                                  <td className="py-3 pr-4 font-medium" data-testid={`text-breakdown-name-${idx}`}>{org.name}</td>
                                  <td className="py-3 pr-4">
                                    <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-breakdown-status-${idx}`}>
                                      {sc.label}
                                    </Badge>
                                  </td>
                                  <td className="py-3 pr-4 text-right" data-testid={`text-breakdown-apts-${idx}`}>{org.apartments ?? 0}</td>
                                  <td className="py-3 pr-4 text-right" data-testid={`text-breakdown-reservations-${idx}`}>{org.reservations ?? 0}</td>
                                  <td className="py-3 pr-4 text-right font-medium" data-testid={`text-breakdown-revenue-${idx}`}>${(org.revenue ?? 0).toLocaleString()}</td>
                                  <td className="py-3 text-right" data-testid={`text-breakdown-month-revenue-${idx}`}>${(org.thisMonthRevenue ?? 0).toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {econStats.roleDistribution && Object.keys(econStats.roleDistribution).length > 0 && (
                  <Card className="border-none shadow-sm" data-testid="card-team-distribution">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-display font-bold mb-4" data-testid="text-team-distribution-heading">Team Distribution</h3>
                      <div className="space-y-3">
                        {Object.entries(econStats.roleDistribution).map(([role, count]: [string, any]) => {
                          const total = Object.values(econStats.roleDistribution).reduce((sum: number, v: any) => sum + (v as number), 0) as number;
                          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                          return (
                            <div key={role} className="space-y-1" data-testid={`row-role-${role}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium capitalize">{role.replace(/_/g, ' ')}</span>
                                <span className="text-sm text-muted-foreground">{count} ({pct}%)</span>
                              </div>
                              <div className="h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-primary transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-display font-bold" data-testid="text-orgs-heading">Organizations</h2>
                  <p className="text-sm text-muted-foreground">Manage tenant organizations and billing</p>
                </div>
                <Dialog open={createOrgOpen} onOpenChange={(open) => {
                  setCreateOrgOpen(open);
                  if (!open) createOrgForm.reset({ name: "", slug: "", status: "active", businessMode: "property_management" });
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="button-create-org">
                      <Plus className="w-4 h-4 mr-2" /> New Organization
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Organization</DialogTitle>
                      <DialogDescription>Add a new tenant organization to the platform.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createOrgForm.handleSubmit(handleCreateOrg)} className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          {...createOrgForm.register("name", {
                            onChange: (e) => {
                              const val = e.target.value;
                              createOrgForm.setValue("slug", slugify(val));
                            },
                          })}
                          placeholder="Acme Properties"
                          data-testid="input-org-name"
                        />
                        {createOrgForm.formState.errors.name && (
                          <p className="text-xs text-destructive mt-1">{createOrgForm.formState.errors.name.message}</p>
                        )}
                      </div>
                      <div>
                        <Label>Slug</Label>
                        <Input {...createOrgForm.register("slug")} placeholder="acme-properties" data-testid="input-org-slug" />
                        {createOrgForm.formState.errors.slug && (
                          <p className="text-xs text-destructive mt-1">{createOrgForm.formState.errors.slug.message}</p>
                        )}
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={createOrgForm.watch("status")}
                          onValueChange={(v) => createOrgForm.setValue("status", v as OrgFormValues["status"])}
                        >
                          <SelectTrigger data-testid="select-org-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Business Mode</Label>
                        <Select
                          value={createOrgForm.watch("businessMode")}
                          onValueChange={(v) => createOrgForm.setValue("businessMode", v as OrgFormValues["businessMode"])}
                        >
                          <SelectTrigger data-testid="select-org-business-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="property_management">Property Management</SelectItem>
                            <SelectItem value="service_business">Service Business</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Determines how costs and revenue are calculated</p>
                      </div>
                      <Button type="submit" className="w-full" disabled={createOrgMutation.isPending} data-testid="button-submit-create-org">
                        {createOrgMutation.isPending ? "Creating..." : "Create Organization"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {orgsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading organizations...</div>
              ) : organizations.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No organizations yet. Create your first organization to get started.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {organizations.map((org: any) => {
                    const config = statusConfig[org.status] || statusConfig.active;
                    const usersCount = getUserCountForOrg(org.id);
                    const orgInvoices = allInvoices.filter((i: any) => i.organizationId === org.id);
                    const orgOutstanding = orgInvoices
                      .filter((i: any) => ['issued', 'overdue'].includes(i.status))
                      .reduce((sum: number, i: any) => sum + parseFloat(i.total || '0'), 0);

                    return (
                      <Card key={org.id} data-testid={`card-org-${org.id}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <Briefcase className="w-5 h-5 text-primary" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold truncate" data-testid={`text-org-name-${org.id}`}>{org.name}</p>
                                  <Badge variant={config.variant} className="no-default-active-elevate" data-testid={`badge-org-status-${org.id}`}>
                                    {config.label}
                                  </Badge>
                                  <Badge variant="outline" className="no-default-active-elevate text-xs" data-testid={`badge-org-mode-${org.id}`}>
                                    {org.businessMode === "service_business" ? "Services" : org.businessMode === "hybrid" ? "Hybrid" : "Property Mgmt"}
                                  </Badge>
                                  {org.stripeCustomerId && (
                                    <Badge variant="outline" className="no-default-active-elevate text-xs text-violet-700 border-violet-300 bg-violet-50 dark:bg-violet-950/20" data-testid={`badge-stripe-customer-${org.id}`}>
                                      <Zap className="w-2.5 h-2.5 mr-1" /> Stripe
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1 flex-wrap">
                                  <span data-testid={`text-org-slug-${org.id}`}>/{org.slug}</span>
                                  <span>{usersCount} {usersCount === 1 ? "user" : "users"}</span>
                                  <span>{org.createdAt ? new Date(org.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : "N/A"}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditOrg(org)}
                                data-testid={`button-edit-org-${org.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" /> Edit
                              </Button>
                              <Button
                                variant={org.status === "suspended" ? "default" : "destructive"}
                                size="sm"
                                onClick={() => handleToggleStatus(org)}
                                disabled={updateOrgMutation.isPending}
                                data-testid={`button-toggle-org-${org.id}`}
                              >
                                {org.status === "suspended" ? "Activate" : "Suspend"}
                              </Button>
                            </div>
                          </div>

                          {/* Billing quick actions */}
                          <div className="flex items-center gap-2 pt-1 border-t flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openFeatures(org)}
                              data-testid={`button-features-${org.id}`}
                            >
                              <ToggleRight className="w-4 h-4 mr-1" /> Features
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openBillingPlan(org)}
                              data-testid={`button-billing-plan-${org.id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-1" /> Billing Plan
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openInvoiceGen(org)}
                              data-testid={`button-generate-invoice-${org.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" /> Generate Invoice
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openInvoices(org)}
                              data-testid={`button-view-invoices-${org.id}`}
                            >
                              <Receipt className="w-4 h-4 mr-1" /> Invoices ({orgInvoices.length})
                            </Button>
                            {orgOutstanding > 0 && (
                              <Badge variant="secondary" className="no-default-active-elevate" data-testid={`badge-outstanding-${org.id}`}>
                                <DollarSign className="w-3 h-3 mr-1" />
                                ${orgOutstanding.toFixed(2)} outstanding
                              </Badge>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setResendOrg(org); setResendEmail(""); setResendOrgOpen(true); }}
                              data-testid={`button-resend-onboarding-${org.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" /> Resend Onboarding
                            </Button>
                            <Button
                              size="sm"
                              className="bg-primary text-primary-foreground hover:bg-primary/90"
                              onClick={() => handleEnterAsAdmin(org)}
                              disabled={impersonatingOrgId === org.id}
                              data-testid={`button-enter-admin-${org.id}`}
                            >
                              <LogIn className="w-4 h-4 mr-1" />
                              {impersonatingOrgId === org.id ? "Entering..." : "Enter as Admin"}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            <Dialog open={editOrgOpen} onOpenChange={(open) => {
              setEditOrgOpen(open);
              if (!open) setEditingOrg(null);
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Organization</DialogTitle>
                  <DialogDescription>Update the organization details.</DialogDescription>
                </DialogHeader>
                <form onSubmit={editOrgForm.handleSubmit(handleUpdateOrg)} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input {...editOrgForm.register("name")} placeholder="Organization name" data-testid="edit-input-org-name" />
                    {editOrgForm.formState.errors.name && (
                      <p className="text-xs text-destructive mt-1">{editOrgForm.formState.errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <Label>Slug</Label>
                    <Input {...editOrgForm.register("slug")} placeholder="organization-slug" data-testid="edit-input-org-slug" />
                    {editOrgForm.formState.errors.slug && (
                      <p className="text-xs text-destructive mt-1">{editOrgForm.formState.errors.slug.message}</p>
                    )}
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={editOrgForm.watch("status")}
                      onValueChange={(v) => editOrgForm.setValue("status", v as OrgFormValues["status"])}
                    >
                      <SelectTrigger data-testid="edit-select-org-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Business Mode</Label>
                    <Select
                      value={editOrgForm.watch("businessMode")}
                      onValueChange={(v) => editOrgForm.setValue("businessMode", v as OrgFormValues["businessMode"])}
                    >
                      <SelectTrigger data-testid="edit-select-org-business-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="property_management">Property Management</SelectItem>
                        <SelectItem value="service_business">Service Business</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Determines how costs and revenue are calculated</p>
                  </div>
                  <Button type="submit" className="w-full" disabled={updateOrgMutation.isPending} data-testid="button-submit-edit-org">
                    {updateOrgMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-display font-bold" data-testid="text-users-heading">Platform Users</h2>
                  <p className="text-sm text-muted-foreground">All users across organizations</p>
                </div>
                <Dialog open={createUserOpen} onOpenChange={(open) => {
                  setCreateUserOpen(open);
                  if (!open) createUserForm.reset({ username: "", password: "", fullName: "", email: "", phone: "", organizationId: undefined as any, role: "super_admin", isAdmin: true });
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="button-create-user">
                      <UserPlus className="w-4 h-4 mr-2" /> Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create User</DialogTitle>
                      <DialogDescription>Add a new super admin user to an organization.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createUserForm.handleSubmit(handleCreateUser)} className="space-y-4">
                      <div>
                        <Label>Organization</Label>
                        <Select
                          value={createUserForm.watch("organizationId")?.toString() || ""}
                          onValueChange={(v) => createUserForm.setValue("organizationId", parseInt(v, 10))}
                        >
                          <SelectTrigger data-testid="select-user-org">
                            <SelectValue placeholder="Select organization" />
                          </SelectTrigger>
                          <SelectContent>
                            {organizations.map((org: any) => (
                              <SelectItem key={org.id} value={org.id.toString()}>{org.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {createUserForm.formState.errors.organizationId && (
                          <p className="text-xs text-destructive mt-1">{createUserForm.formState.errors.organizationId.message}</p>
                        )}
                      </div>
                      <div>
                        <Label>Full Name</Label>
                        <Input {...createUserForm.register("fullName")} placeholder="John Doe" data-testid="input-user-fullname" />
                      </div>
                      <div>
                        <Label>Username</Label>
                        <Input {...createUserForm.register("username")} placeholder="johndoe" data-testid="input-user-username" />
                        {createUserForm.formState.errors.username && (
                          <p className="text-xs text-destructive mt-1">{createUserForm.formState.errors.username.message}</p>
                        )}
                      </div>
                      <div>
                        <Label>Password</Label>
                        <Input type="password" {...createUserForm.register("password")} placeholder="Enter password" data-testid="input-user-password" />
                        {createUserForm.formState.errors.password && (
                          <p className="text-xs text-destructive mt-1">{createUserForm.formState.errors.password.message}</p>
                        )}
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" {...createUserForm.register("email")} placeholder="john@example.com" data-testid="input-user-email" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input {...createUserForm.register("phone")} placeholder="+1 555-0100" data-testid="input-user-phone" />
                      </div>
                      <Button type="submit" className="w-full" disabled={createUserMutation.isPending} data-testid="button-submit-create-user">
                        {createUserMutation.isPending ? "Creating..." : "Create User"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {usersLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading users...</div>
              ) : platformUsers.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No users found. Add users to organizations to get started.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {platformUsers.map((user: any) => (
                    <Card key={user.id} data-testid={`card-user-${user.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <Users className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate" data-testid={`text-user-name-${user.id}`}>{user.fullName || user.username}</p>
                              <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="no-default-active-elevate" data-testid={`badge-user-role-${user.id}`}>
                            {user.role}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <p data-testid={`text-user-org-${user.id}`}>Org: {getOrgName(user.organizationId)}</p>
                          {user.email && <p>{user.email}</p>}
                          {user.phone && <p>{user.phone}</p>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'invoices' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-display font-bold" data-testid="text-all-invoices-heading">All Invoices</h2>
              <p className="text-sm text-muted-foreground">Billing invoices across all organizations</p>
            </div>

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
              <Card className="border-none shadow-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Billed</p>
                  <p className="text-2xl font-bold mt-1" data-testid="text-total-billed">${totalBilled.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400" data-testid="text-total-paid">${totalPaid.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400" data-testid="text-total-outstanding">${totalOutstanding.toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            {allInvoices.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  No invoices yet. Generate invoices from the Organizations tab.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {allInvoices.map((inv: any) => {
                  const sc = invoiceStatusConfig[inv.status] || invoiceStatusConfig.draft;
                  const orgName = getOrgName(inv.organizationId);
                  return (
                    <Card key={inv.id} data-testid={`card-all-invoice-${inv.id}`}>
                      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium" data-testid={`text-all-invoice-org-${inv.id}`}>{orgName}</p>
                            <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-all-invoice-status-${inv.id}`}>
                              {sc.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(inv.periodStart).toLocaleDateString("en-US", { timeZone: "UTC" })} - {new Date(inv.periodEnd).toLocaleDateString("en-US", { timeZone: "UTC" })}
                            <span className="mx-2">|</span>
                            {billingModelLabels[inv.model] || inv.model}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold" data-testid={`text-all-invoice-total-${inv.id}`}>
                            ${parseFloat(inv.total || '0').toFixed(2)}
                          </p>
                          {inv.dueDate && (
                            <p className="text-xs text-muted-foreground">Due: {new Date(inv.dueDate).toLocaleDateString("en-US", { timeZone: "UTC" })}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-display font-bold" data-testid="text-users-heading">User Accounts</h2>
              <p className="text-sm text-muted-foreground">Browse user accounts across all organizations</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-sm font-medium">Organization:</Label>
              <Select value={selectedOrgUsers?.toString() || "none"} onValueChange={(v) => setSelectedOrgUsers(v === "none" ? null : Number(v))}>
                <SelectTrigger className="w-64" data-testid="select-org-users-filter">
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select organization...</SelectItem>
                  {organizations.map((org: any) => (
                    <SelectItem key={org.id} value={org.id.toString()}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!selectedOrgUsers ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  Select an organization above to view its user accounts.
                </CardContent>
              </Card>
            ) : orgUsersLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading users...</div>
            ) : orgUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  No users found for this organization.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{orgUsers.length} user(s) found</p>
                <div className="grid gap-3">
                  {orgUsers.map((u: any) => (
                    <Card key={u.id} data-testid={`card-platform-user-${u.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                              {(u.fullName || u.username || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium" data-testid={`text-user-name-${u.id}`}>{u.fullName || u.username}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>@{u.username}</span>
                                {u.email && <span>{u.email}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="no-default-active-elevate text-xs" data-testid={`badge-user-role-${u.id}`}>
                              {u.role === 'owner' ? 'Property Manager' : u.role?.replace('_', ' ')}
                            </Badge>
                            <Button variant="outline" size="sm" onClick={() => setViewProfileUser(u)} data-testid={`button-view-profile-${u.id}`}>
                              View Profile
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {viewProfileUser && (
              <Dialog open={viewProfileUser !== null} onOpenChange={(open) => { if (!open) setViewProfileUser(null); }}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle data-testid="text-profile-dialog-title">{viewProfileUser.fullName || viewProfileUser.username} - Profile</DialogTitle>
                    <DialogDescription>
                      Role: {viewProfileUser.role === 'owner' ? 'Property Manager' : viewProfileUser.role?.replace('_', ' ')} | Username: @{viewProfileUser.username}
                    </DialogDescription>
                  </DialogHeader>
                  {!viewProfileData ? (
                    <div className="py-8 text-center text-muted-foreground">No profile data submitted yet.</div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Address</p>
                          <p>{viewProfileData.address || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">City / State / Zip</p>
                          <p>{[viewProfileData.city, viewProfileData.state, viewProfileData.zipCode].filter(Boolean).join(', ') || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Date of Birth</p>
                          <p>{viewProfileData.dateOfBirth || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Emergency Contact</p>
                          <p>{viewProfileData.emergencyContactName || '-'} {viewProfileData.emergencyContactPhone ? `(${viewProfileData.emergencyContactPhone})` : ''}</p>
                        </div>
                      </div>
                      {(viewProfileData.w9LegalName || viewProfileData.w9SsnLast4 || viewProfileData.w9EinLast4) && (
                        <div className="border-t pt-3">
                          <p className="font-medium text-sm mb-2">W-9 / Tax Info</p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Legal Name</p>
                              <p>{viewProfileData.w9LegalName || '-'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Tax Classification</p>
                              <p>{viewProfileData.w9TaxClassification || '-'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">SSN</p>
                              <p>{viewProfileData.w9Ssn || (viewProfileData.w9SsnLast4 ? `***-**-${viewProfileData.w9SsnLast4}` : '-')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">EIN</p>
                              <p>{viewProfileData.w9Ein || (viewProfileData.w9EinLast4 ? `**-***${viewProfileData.w9EinLast4}` : '-')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {(viewProfileData.bankName || viewProfileData.bankAccountNumberLast4) && (
                        <div className="border-t pt-3">
                          <p className="font-medium text-sm mb-2">Bank Info</p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Bank Name</p>
                              <p>{viewProfileData.bankName || '-'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Account Number</p>
                              <p>{viewProfileData.bankAccountNumber || (viewProfileData.bankAccountNumberLast4 ? `****${viewProfileData.bankAccountNumberLast4}` : '-')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Routing Number</p>
                              <p>{viewProfileData.bankRoutingNumber || (viewProfileData.bankRoutingNumberLast4 ? `****${viewProfileData.bankRoutingNumberLast4}` : '-')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Account Type</p>
                              <p>{viewProfileData.bankAccountType || '-'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="border-t pt-3">
                        <p className="font-medium text-sm mb-2">Onboarding Status</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={viewProfileData.personalInfoCompleted ? "default" : "outline"} className="no-default-active-elevate text-xs">Personal Info</Badge>
                          <Badge variant={viewProfileData.idUploadCompleted ? "default" : "outline"} className="no-default-active-elevate text-xs">ID Upload</Badge>
                          <Badge variant={viewProfileData.w9Completed ? "default" : "outline"} className="no-default-active-elevate text-xs">W-9</Badge>
                          <Badge variant={viewProfileData.bankInfoCompleted ? "default" : "outline"} className="no-default-active-elevate text-xs">Bank Info</Badge>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {activeTab === 'leads' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-display font-bold" data-testid="text-leads-heading">Host Leads</h2>
              <p className="text-sm text-muted-foreground">Prospective property managers who want to join</p>
            </div>

            {hostLeads.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  No host leads yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {hostLeads.map((lead: any) => {
                  const leadStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
                    new: { label: "New", variant: "default" },
                    contacted: { label: "Contacted", variant: "secondary" },
                    qualified: { label: "Qualified", variant: "outline" },
                    onboarded: { label: "Onboarded", variant: "default" },
                    declined: { label: "Declined", variant: "destructive" },
                  };
                  const lsc = leadStatusConfig[lead.status] || leadStatusConfig.new;
                  return (
                    <Card key={lead.id} data-testid={`card-lead-${lead.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <MessageSquare className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold" data-testid={`text-lead-name-${lead.id}`}>{lead.name}</p>
                                <Badge variant={lsc.variant} className="no-default-active-elevate" data-testid={`badge-lead-status-${lead.id}`}>
                                  {lsc.label}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                                {lead.email && <span data-testid={`text-lead-email-${lead.id}`}>{lead.email}</span>}
                                {lead.phone && <span data-testid={`text-lead-phone-${lead.id}`}>{lead.phone}</span>}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                                {lead.company && <span data-testid={`text-lead-company-${lead.id}`}>{lead.company}</span>}
                                {lead.propertyCount != null && <span data-testid={`text-lead-properties-${lead.id}`}>{lead.propertyCount} properties</span>}
                                {lead.location && <span data-testid={`text-lead-location-${lead.id}`}>{lead.location}</span>}
                              </div>
                              {lead.message && (
                                <p className="text-sm text-muted-foreground mt-1" data-testid={`text-lead-message-${lead.id}`}>{lead.message}</p>
                              )}
                              {lead.createdAt && (
                                <p className="text-xs text-muted-foreground" data-testid={`text-lead-date-${lead.id}`}>
                                  {new Date(lead.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="shrink-0">
                            <Select
                              value={lead.status || 'new'}
                              onValueChange={(v) => updateLeadMutation.mutate({ id: lead.id, status: v })}
                            >
                              <SelectTrigger className="w-36" data-testid={`select-lead-status-${lead.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="qualified">Qualified</SelectItem>
                                <SelectItem value="onboarded">Onboarded</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'marketplace' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-display font-bold" data-testid="text-marketplace-heading">Marketplace Management</h2>
              <p className="text-sm text-muted-foreground">Manage marketplace listings across all organizations</p>
            </div>

            {marketplaceStats && (
              <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
                <Card className="border-none shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Total Apartments</p>
                    <p className="text-2xl font-bold mt-1" data-testid="text-mp-total">{marketplaceStats.totalApartments}</p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Visible Listings</p>
                    <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400" data-testid="text-mp-visible">{marketplaceStats.visibleListings}</p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Featured</p>
                    <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400" data-testid="text-mp-featured">{marketplaceStats.featuredListings}</p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">With Price</p>
                    <p className="text-2xl font-bold mt-1" data-testid="text-mp-priced">{marketplaceStats.listingsWithPrice}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <Select value={marketplaceOrgFilter} onValueChange={setMarketplaceOrgFilter}>
                <SelectTrigger className="w-48" data-testid="select-mp-org-filter">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org: any) => (
                    <SelectItem key={org.id} value={String(org.id)}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={marketplaceVisFilter} onValueChange={setMarketplaceVisFilter}>
                <SelectTrigger className="w-40" data-testid="select-mp-vis-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="visible">Visible</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                  <SelectItem value="featured">Featured</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {marketplaceLoading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <Card key={i}><CardContent className="p-4"><div className="h-12 bg-muted animate-pulse rounded" /></CardContent></Card>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {marketplaceApts
                  .filter((apt: any) => {
                    if (marketplaceOrgFilter !== "all" && String(apt.organizationId) !== marketplaceOrgFilter) return false;
                    if (marketplaceVisFilter === "visible" && !apt.marketplaceVisible) return false;
                    if (marketplaceVisFilter === "hidden" && apt.marketplaceVisible) return false;
                    if (marketplaceVisFilter === "featured" && !apt.marketplaceFeatured) return false;
                    return true;
                  })
                  .map((apt: any) => (
                    <Card key={apt.id} data-testid={`card-mp-apt-${apt.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {apt.heroImage ? (
                              <img src={apt.heroImage} alt={apt.name} className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                                <Home className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium truncate" data-testid={`text-mp-apt-name-${apt.id}`}>{apt.name}</p>
                                {apt.marketplaceVisible && (
                                  <Badge variant="default" className="no-default-active-elevate">Visible</Badge>
                                )}
                                {apt.marketplaceFeatured && (
                                  <Badge variant="secondary" className="no-default-active-elevate">Featured</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">
                                {apt.organizationName} {apt.location ? `| ${apt.location}` : ""} {apt.propertyType ? `| ${apt.propertyType}` : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Visible</span>
                              <Switch
                                checked={apt.marketplaceVisible || false}
                                onCheckedChange={(checked) => toggleMarketplaceMutation.mutate({ id: apt.id, field: "marketplaceVisible", value: checked })}
                                data-testid={`switch-mp-visible-${apt.id}`}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Featured</span>
                              <Switch
                                checked={apt.marketplaceFeatured || false}
                                onCheckedChange={(checked) => toggleMarketplaceMutation.mutate({ id: apt.id, field: "marketplaceFeatured", value: checked })}
                                data-testid={`switch-mp-featured-${apt.id}`}
                              />
                            </div>
                            {apt.marketplacePrice && (
                              <Badge variant="outline" className="no-default-active-elevate">
                                <DollarSign className="w-3 h-3 mr-1" />{parseFloat(apt.marketplacePrice).toFixed(0)}/night
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditMarketplaceApt(apt); setEditMarketplaceOpen(true); }}
                              data-testid={`button-mp-edit-${apt.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                {marketplaceApts.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      No apartments found. Create apartments in organizations first.
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Change Password Dialog */}
      <Dialog open={changePassOpen} onOpenChange={open => { setChangePassOpen(open); if (!open) setChangePassForm({ current: "", next: "", confirm: "" }); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Platform Password</DialogTitle>
            <DialogDescription>Use a strong password of at least 8 characters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Current Password</Label>
              <Input type="password" value={changePassForm.current} onChange={e => setChangePassForm(p => ({ ...p, current: e.target.value }))} data-testid="input-current-password" />
            </div>
            <div>
              <Label>New Password</Label>
              <Input type="password" value={changePassForm.next} onChange={e => setChangePassForm(p => ({ ...p, next: e.target.value }))} data-testid="input-new-password" />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input type="password" value={changePassForm.confirm} onChange={e => setChangePassForm(p => ({ ...p, confirm: e.target.value }))} data-testid="input-confirm-password" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setChangePassOpen(false)} disabled={changePassLoading}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={changePassLoading || !changePassForm.current || !changePassForm.next || !changePassForm.confirm} data-testid="button-save-password">
                {changePassLoading ? "Saving..." : "Change Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Onboarding Dialog */}
      <Dialog open={resendOrgOpen} onOpenChange={open => { setResendOrgOpen(open); if (!open) { setResendOrg(null); setResendEmail(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Resend Onboarding Email</DialogTitle>
            <DialogDescription>{resendOrg?.name} — override the email address if needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Send to (email)</Label>
              <Input type="email" placeholder="Leave blank to use org's admin email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} data-testid="input-resend-email" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResendOrgOpen(false)}>Cancel</Button>
              <Button onClick={handleResendOnboarding} data-testid="button-confirm-resend">
                <Send className="w-4 h-4 mr-2" /> Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Marketplace Edit Dialog */}
      {editMarketplaceApt && (
        <MarketplaceEditDialog
          apartment={editMarketplaceApt}
          open={editMarketplaceOpen}
          onOpenChange={(open) => { setEditMarketplaceOpen(open); if (!open) setEditMarketplaceApt(null); }}
          onSave={(data) => updateMarketplaceMutation.mutate({ id: editMarketplaceApt.id, data })}
          isPending={updateMarketplaceMutation.isPending}
        />
      )}

      {/* Billing Plan Dialog */}
      {billingOrg && (
        <BillingPlanDialog org={billingOrg} open={billingOrgOpen} onOpenChange={(open) => { setBillingOrgOpen(open); if (!open) setBillingOrg(null); }} />
      )}

      {/* Invoice Generate Dialog */}
      {invoiceGenOrg && (
        <InvoiceGenerateDialog org={invoiceGenOrg} open={invoiceGenOrgOpen} onOpenChange={(open) => { setInvoiceGenOrgOpen(open); if (!open) setInvoiceGenOrg(null); }} />
      )}

      {/* Org Invoices Dialog */}
      {invoicesOrg && (
        <OrgInvoicesDialog org={invoicesOrg} open={invoicesOrgOpen} onOpenChange={(open) => { setInvoicesOrgOpen(open); if (!open) setInvoicesOrg(null); }} />
      )}

      {/* Feature Flags Dialog */}
      {featuresOrg && (
        <FeatureFlagsDialog org={featuresOrg} open={featuresOrgOpen} onOpenChange={(open) => { setFeaturesOrgOpen(open); if (!open) setFeaturesOrg(null); }} />
      )}
    </div>
  );
}
